/**
 * Referral-code capture for creator links.
 *
 * A creator's link-in-bio is `defera.app/?ref=JAY20`. Someone arriving that way
 * has no reason to also remember to type the code at checkout — and most won't
 * — so the code is captured on arrival, parked in localStorage, and applied to
 * their cart automatically once one exists.
 *
 * Why storage rather than routing state: the gap between the click and the cart
 * is long and lossy. The customer lands, searches flights, signs up (a full
 * redirect through Supabase auth), picks seats, and only then reaches Review.
 * Anything held in memory or in the URL is gone several navigations earlier.
 *
 * The stored value is a claim, never an authority: it is only ever replayed to
 * the server's own validation endpoint, which re-checks the code against the
 * database. A tampered localStorage entry buys nothing but a rejection.
 */
import { useEffect } from 'react';
import { sanitizeDiscountCode, DISCOUNT_REF_PARAM } from '@/utils/discountCodes';

const STORAGE_KEY = 'defera_referral_code';
const VISITOR_KEY = 'defera_visitor_id';

/**
 * How long a click keeps its claim. Thirty days is the affiliate-tracking
 * convention and comfortably covers Defera's browse-to-book gap, which runs
 * long because trips are planned weeks ahead.
 */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A stable, random, first-party id for this browser.
 *
 * Deliberately NOT a fingerprint: it is a random value the browser generates
 * for itself and stores locally, carrying no information about the person. It
 * exists so the same visitor arriving twice counts as one person reached, and
 * so a click can later be joined to the cart it produced.
 *
 * @returns {string|null} null when storage is unavailable (private browsing)
 */
export function getVisitorId() {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() ?? `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Capture `?ref=CODE` from the current URL and report the arrival. Call once,
 * high in the tree, so it runs no matter which page the creator linked to.
 *
 * Last link wins: someone who clicks a second creator's link is expressing a
 * fresher intent, and silently honouring the older code would attribute the
 * booking to whoever they happened to click first.
 *
 * The click beacon fires from here — client-side, after hydration — rather than
 * from the server parsing `?ref=`. That is what keeps the number honest: shared
 * links are hit constantly by link-preview bots, crawlers and security
 * scanners, and virtually none of them execute JavaScript. Counting server-side
 * would have inflated every creator's reach with traffic that was never a
 * person.
 */
export function useReferralCapture() {
  useEffect(() => {
    let code = null;
    try {
      const params = new URLSearchParams(window.location.search);
      code = sanitizeDiscountCode(params.get(DISCOUNT_REF_PARAM));
      if (!code) return;
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ code, capturedAt: Date.now() })
      );
    } catch {
      // Private browsing / storage disabled. The customer can still type the
      // code; only the automatic path is lost.
    }
    if (!code) return;

    // Fire-and-forget. The endpoint answers before it writes, and a failure
    // here must never be visible to someone who is just loading the site.
    try {
      fetch('/api/referral/click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          visitor_id: getVisitorId(),
          landing_path: window.location.pathname,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* nothing to do — tracking is never worth an error the customer sees */
    }
  }, []);
}

/**
 * The live captured code, or null when there is none or it has aged out.
 * @returns {string|null}
 */
export function readStoredReferralCode() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { code, capturedAt } = JSON.parse(raw);
    if (!code || !capturedAt) return null;
    if (Date.now() - capturedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return sanitizeDiscountCode(code) || null;
  } catch {
    return null;
  }
}

/**
 * Drop the stored code. Called once it has been applied to a cart, and also
 * when the server rejects it — a code that failed validation would otherwise
 * retry on every cart the customer ever builds.
 */
export function clearStoredReferralCode() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
